import SEO from "../components/SEO";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useTheme, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Sun, Moon, ArrowLeft, Shield, Mail, Lock } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

export default function AdminAuth() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (!loginData.password || !loginData.password.trim()) {
      toast.error("Please enter your password");
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/admin/login`, {
        email: loginData.email.trim(),
        password: loginData.password
      });

      login(response.data.access_token, response.data.user, response.data.user_type);
      toast.success("Welcome, Admin!");
      navigate("/nedhaavu/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO page="admin" />
      
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Home</span>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-full"
          data-testid="theme-toggle"
        >
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/">
              <img src={LOGO_URL} alt="TyreMatch" className="h-16 mx-auto mb-4" />
            </Link>
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Shield className="h-4 w-4" />
              Admin Access
            </div>
            <h1 className="font-heading text-3xl font-bold mb-2">Admin Portal</h1>
            <p className="text-muted-foreground">
              Platform management dashboard
            </p>
          </div>

          <Card className="glass-card" data-testid="admin-auth-card">
            <CardHeader>
              <CardTitle>Admin Login</CardTitle>
              <CardDescription>
                Access the platform management dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="Enter your email"
                      className="pl-10"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      data-testid="admin-login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="admin-login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground"
                  disabled={loading}
                  data-testid="admin-login-submit-btn"
                >
                  {loading ? "Logging in..." : "Login"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Authorized personnel only
          </p>
        </div>
      </main>
    </div>
  );
}
